import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    BreadcrumbItem,
    Breadcrumbs,
    PageHeader,
} from '@vnetwork/datacentral-ui/layout'
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    IconButton,
} from '@vnetwork/datacentral-ui/primitives'
import type { TabItem } from '@vnetwork/datacentral-ui/primitives'
import type { Action } from '../../ButtonGroupActions'
import { cn } from '../../../lib/utils'

const PAGE_TRAIL_ID = 'page-trail'
const PAGE_TAB_BAR_ID = 'page-tab-bar'

/**
 * Topbar slot for the page trail. The app layout renders it once in the
 * topbar; a `layout="bar"` header portals its breadcrumb and status into it.
 */
export const PageTrailSlot = () => (
    <div id={PAGE_TRAIL_ID} className="flex min-w-0 flex-1 items-center" />
)

/**
 * Slot above the page content for the page's tab bar (the shell's `notice`
 * position, inside the scrolling column). A `layout="bar"` header portals its
 * tabs and actions into it, so the bar spans the column and sticks on scroll.
 */
export const PageTabBarSlot = () => <div id={PAGE_TAB_BAR_ID} />

/** A shell slot once mounted, or null outside the app layout. */
const usePageSlot = (id: string, enabled: boolean) => {
    const [slot, setSlot] = useState<HTMLElement | null>(null)
    useLayoutEffect(() => {
        if (enabled) setSlot(document.getElementById(id))
    }, [id, enabled])
    return slot
}

export interface SubHeadersProps {
    /**
     * Trail to the current page; the last entry is the page title. Parent
     * entries render as breadcrumbs (none for a top-level page).
     */
    breadcrumbs?: {
        title: string
        path: string
    }[]
    /** Overrides the title taken from the last breadcrumb. */
    title?: React.ReactNode
    /** One muted line under the title. */
    description?: React.ReactNode
    /** Small label above the title (section name). */
    eyebrow?: React.ReactNode
    /**
     * Leading icon before the title. Overview / landing pages only (a
     * dashboard); zone and section pages use `eyebrow` instead.
     */
    icon?: React.ReactNode
    /** Status / key facts next to the title (e.g. a `StatusPill`). */
    status?: React.ReactNode
    /**
     * Page actions. The first `maxVisibleActions` render as buttons, the rest
     * go in a ⋯ menu so a header never becomes a wall of buttons.
     */
    actions?: Action[]
    /** @default 2 */
    maxVisibleActions?: number
    /** Extra header nodes after the actions. */
    components?: React.ReactNode[]
    /** Hide the parent trail (create / edit pages that have a Cancel action). */
    hideBreadcrumbs?: boolean
    /**
     * The app's home route, dropped from the trail (the sidebar already links
     * it). Defaults to '/', the shell's `paths.home`; a prop rather than an
     * import so this package does not depend on the app's merged `paths`.
     */
    homePath?: string
    /**
     * - "default": today's header, unchanged.
     * - "compact": GitHub-style (#87) — DS `PageHeader size="compact"`, slash
     *   breadcrumbs, `status` and `meta` on one row under the title. Leave
     *   `eyebrow` and `description` out; put the facts in `meta` instead.
     * Opt-in per page so pages that do not pass it render exactly as before.
     * @default "default"
     */
    size?: 'default' | 'compact'
    /** Muted text after the title, e.g. a record number. `compact` only. */
    titleSuffix?: React.ReactNode
    /** Facts after `status`, joined with "·". `compact` only. */
    meta?: React.ReactNode[]
    /**
     * The page's search / filter / view row (e.g. a `FilterBar
     * layout="full"`, or tabs with a period picker). Rendered under the title
     * as part of the same band, with one fixed gap, instead of each page
     * spacing its own row below the header.
     */
    toolbar?: React.ReactNode
    /**
     * - "header": the `PageHeader` above (title row).
     * - "bar": no visible title. The page is named by its breadcrumb (the
     *   last entry is the current page, followed by `status`); the actions sit
     *   at the right of the `toolbar` row (tabs), or of the breadcrumb row when
     *   there is no toolbar. `icon`, `eyebrow`, `description` and `meta` are
     *   not rendered: record facts belong in the page's summary rail.
     * Opt-in per page so pages that do not pass it render exactly as before.
     * @default "header"
     */
    layout?: 'header' | 'bar'
    /**
     * The page's sub-sections (`useTabs().TabProps`). `bar` only: rendered as
     * the tab bar above the content, with the actions at its right end. Bar
     * tabs are text-only pills (Resource page template): item icons are not
     * rendered.
     */
    tabs?: {
        value?: string
        onValueChange: (value: string) => void
        items: TabItem[]
        'data-tour'?: string
    }
    className?: string
}

/**
 * Page header on the DS `PageHeader`: parent trail, title with status, one
 * line of description, and at most a couple of visible actions.
 */
const SubHeaders: React.FC<SubHeadersProps> = ({
    actions = [],
    breadcrumbs = [],
    components = [],
    title,
    description,
    eyebrow,
    icon,
    status,
    maxVisibleActions = 2,
    hideBreadcrumbs = false,
    homePath = '/',
    size = 'default',
    layout = 'header',
    tabs,
    titleSuffix,
    meta,
    toolbar,
    className,
}) => {
    const { t } = useTranslation()
    const trailSlot = usePageSlot(PAGE_TRAIL_ID, layout === 'bar')
    const tabBarSlot = usePageSlot(PAGE_TAB_BAR_ID, layout === 'bar')
    // The tab bar is a compact strip: its buttons are small.
    const buttonSize = layout === 'bar' ? 'sm' : undefined
    const current = breadcrumbs[breadcrumbs.length - 1]
    // The sidebar already says where we are; only real parents form a trail.
    const trail = hideBreadcrumbs
        ? []
        : breadcrumbs.slice(0, -1).filter((b) => b.path !== homePath)

    const visible = actions.slice(0, maxVisibleActions)
    const overflow = actions.slice(maxVisibleActions)

    const variantOf = (action: Action, index: number) =>
        action.variant ?? (index === 0 ? 'primary' : 'secondary')

    const button = (action: Action, index: number) => (
        <Button
            key={index}
            size={buttonSize}
            variant={variantOf(action, index)}
            startIcon={action.icon}
            disabled={action.disabled}
            loading={action.loading}
            onClick={action.onClick}
            data-tour={action['data-tour']}
        >
            {action.label}
        </Button>
    )

    const menu =
        overflow.length > 0 ? (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <IconButton
                        variant="secondary"
                        size={buttonSize}
                        aria-label={t('more-actions')}
                    >
                        <MoreHorizontal size={16} />
                    </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {overflow.map((action, index) => (
                        <DropdownMenuItem
                            key={index}
                            tone={
                                action.variant === 'danger'
                                    ? 'danger'
                                    : 'default'
                            }
                            disabled={action.disabled || action.loading}
                            onSelect={action.onClick}
                            data-tour={action['data-tour']}
                        >
                            {action.icon}
                            {action.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        ) : null

    const extra = components.map((component, index) => (
        <React.Fragment key={`c-${index}`}>{component}</React.Fragment>
    ))

    // compact (GitHub-style) and bar: secondary actions and extra controls
    // first, the ⋯ menu, then the primary action at the far right.
    const isPrimary = (action: Action, index: number) =>
        variantOf(action, index) === 'primary'

    const pageActions =
        actions.length > 0 || components.length > 0 ? (
            size === 'compact' || layout === 'bar' ? (
                <>
                    {visible.map((a, i) =>
                        isPrimary(a, i) ? null : button(a, i)
                    )}
                    {extra}
                    {menu}
                    {visible.map((a, i) =>
                        isPrimary(a, i) ? button(a, i) : null
                    )}
                </>
            ) : (
                <>
                    {visible.map(button)}
                    {menu}
                    {extra}
                </>
            )
        ) : undefined

    const crumbs =
        trail.length > 0 ? (
            <Breadcrumbs separator={size === 'compact' ? 'slash' : undefined}>
                {trail.map(({ path, title }) => (
                    <BreadcrumbItem key={path} asChild>
                        <Link
                            to={path}
                            className="inline-block first-letter:uppercase"
                        >
                            {title}
                        </Link>
                    </BreadcrumbItem>
                ))}
            </Breadcrumbs>
        ) : undefined

    const pageTitle = title ?? current?.title ?? t('dashboard')

    if (layout === 'bar') {
        // The trail sits in the topbar, next to ~6 icon buttons, so it never
        // wraps: one line, each crumb truncates (min-w-0 down the whole chain).
        // Below lg (phone, or tablet with the sidebar open) only the current page
        // shows; parents and the status return from lg.
        const crumbItems = [
            ...trail.map(({ path, title }) => (
                <BreadcrumbItem key={path} asChild>
                    <Link
                        to={path}
                        className="block truncate first-letter:uppercase"
                    >
                        {title}
                    </Link>
                </BreadcrumbItem>
            )),
            <BreadcrumbItem
                key="current"
                current
                className="flex min-w-0 items-baseline gap-1"
            >
                <span className="block min-w-0 truncate first-letter:uppercase">
                    {pageTitle}
                </span>
                {titleSuffix != null && (
                    <span className="shrink-0">{titleSuffix}</span>
                )}
            </BreadcrumbItem>,
        ]
        const trailBar = (
            <div className="flex min-w-0 items-center gap-2">
                {/* The page keeps one heading for assistive tech. */}
                <h1 className="sr-only">
                    {pageTitle}
                    {titleSuffix != null && <> {titleSuffix}</>}
                </h1>
                <Breadcrumbs className="min-w-0 [&>ol]:flex-nowrap [&>ol>li:not([aria-hidden])]:min-w-0 [&>ol>li[aria-hidden]]:shrink-0 max-lg:[&>ol>li:not(:last-child)]:hidden">
                    {crumbItems}
                </Breadcrumbs>
                {status && (
                    // Hidden below lg (phones and the open-sidebar tablet
                    // width have no room next to the topbar icons); at lg it
                    // may shrink instead of pushing over them.
                    <span className="hidden min-w-0 items-center gap-2 lg:inline-flex max-xl:overflow-hidden xl:shrink-0">
                        {status}
                    </span>
                )}
            </div>
        )
        // Every control in the bar (buttons, pickers, selects) takes the
        // small control height, so the row lines up.
        const actionGroup = pageActions && (
            <div
                className={cn(
                    'flex items-center gap-2 [--vn-control-height-md:var(--vn-control-height-sm)]',
                    // With tabs the actions keep their width and the tabs
                    // scroll. Without, a phone row of buttons wider than the
                    // screen scrolls sideways instead of being clipped.
                    tabs != null
                        ? 'shrink-0 flex-wrap justify-end'
                        : 'ml-auto min-w-0 overflow-x-auto'
                )}
            >
                {pageActions}
            </div>
        )
        // Same height as the sidebar header (search box + padding + rule),
        // so the two bottom rules line up across the page.
        const tabBar = (tabs != null || actionGroup != null) && (
            <div className="sticky top-0 z-[var(--vn-z-sticky)] flex h-[var(--vn-chrome-row-height)] items-center justify-between gap-3 border-b border-[var(--vn-border-default)] bg-[var(--vn-bg-surface)] px-[var(--vn-page-gutter,var(--vn-space-6))]">
                <div
                    className="flex min-w-0 items-center gap-1 overflow-x-auto"
                    data-tour={tabs?.['data-tour']}
                >
                    {tabs?.items
                        .filter((item) => !item.hidden)
                        .map((item) => {
                            const active = item.value === tabs.value
                            return (
                                <Button
                                    key={item.value}
                                    variant="ghost"
                                    size="sm"
                                    aria-current={active ? 'page' : undefined}
                                    disabled={item.disabled}
                                    endIcon={item.endIcon}
                                    onClick={() =>
                                        tabs.onValueChange(item.value)
                                    }
                                    className={cn(
                                        'shrink-0 text-[var(--vn-fg-muted)]',
                                        active &&
                                            'bg-[var(--vn-bg-active)] text-[var(--vn-fg-default)] hover:bg-[var(--vn-bg-active)]'
                                    )}
                                >
                                    {item.label}
                                </Button>
                            )
                        })}
                </div>
                {actionGroup}
            </div>
        )

        return (
            <>
                {/* The trail lives in the topbar and the tab bar above the
                    content; outside the app layout (no slots) both stay at
                    the top of the page. */}
                {trailSlot ? createPortal(trailBar, trailSlot) : trailBar}
                {tabBar &&
                    (tabBarSlot ? createPortal(tabBar, tabBarSlot) : tabBar)}
                {toolbar != null && (
                    <div
                        className={cn(
                            'mb-[var(--vn-page-gap,var(--vn-space-5))]',
                            className
                        )}
                    >
                        {toolbar}
                    </div>
                )}
            </>
        )
    }

    const header =
        size === 'compact' ? (
            <PageHeader
                size="compact"
                // In the band, the band's gap separates header and toolbar.
                className={cn(toolbar != null && 'mb-0', className)}
                icon={icon}
                title={
                    <span className="inline-block first-letter:uppercase">
                        {pageTitle}
                    </span>
                }
                titleSuffix={titleSuffix}
                status={status}
                meta={meta}
                eyebrow={eyebrow}
                description={description}
                breadcrumbs={crumbs}
                actions={pageActions}
            />
        ) : (
            <PageHeader
                className={cn(
                    // DS PageHeader keeps actions on one line at full width; with
                    // more than two buttons let them wrap (right-aligned) so the
                    // title and description keep their room.
                    visible.length > 2 &&
                        '[&>div:last-child>div:first-child]:flex-[1_0_18rem] [&>div:last-child>div:last-child]:shrink [&>div:last-child>div:last-child]:flex-wrap [&>div:last-child>div:last-child]:justify-end',
                    toolbar != null && 'mb-0',
                    className
                )}
                eyebrow={eyebrow}
                icon={icon}
                title={
                    <span className="inline-flex flex-wrap items-center gap-2">
                        <span className="inline-block first-letter:uppercase">
                            {pageTitle}
                        </span>
                        {status && (
                            // Pills / tags keep their own weight, not the h1's.
                            <span className="inline-flex flex-wrap items-center gap-2 text-sm font-normal">
                                {status}
                            </span>
                        )}
                    </span>
                }
                description={description}
                breadcrumbs={crumbs}
                actions={pageActions}
            />
        )

    if (toolbar == null) return header

    // Header + search/filter row as one band (#87): the DS page gap between
    // them and after the band, whatever the page does around it.
    return (
        <div className="flex flex-col gap-[var(--vn-page-gap,var(--vn-space-5))] mb-[var(--vn-page-gap,var(--vn-space-5))]">
            {header}
            {toolbar}
        </div>
    )
}

export default SubHeaders
